import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tag, Typography } from 'antd';
import { Card, CardContent, CardFooter, CardHeader } from '@/shared/ui/Card';
import { SettingsContext } from '@/state/contexts';
import {
  useGetTemplatesQuery,
  useGetWorksheetsQuery,
} from '@/state/slices/reports';
import type { Template } from '@/models/template';
import { formatDate } from '../utils/formatDate';
import { recentTemplates } from './utils/recentTemplates';
import styles from './TemplatesLanding.module.css';

const { Text } = Typography;

function nameOf(list: Template[] | undefined, uuid: string) {
  if (!list) return '—';
  return list.find((t) => t.uuid === uuid)?.name ?? 'Not set';
}

/** Entry point of the Templates area: counts, defaults and recent edits. */
export function TemplatesLanding() {
  const navigate = useNavigate();
  const { settings } = useContext(SettingsContext);
  const { data: worksheets } = useGetWorksheetsQuery();
  const { data: reports } = useGetTemplatesQuery();

  const recent = useMemo(
    () => recentTemplates(worksheets?.results, reports?.results),
    [worksheets, reports]
  );

  const cards = [
    {
      title: 'Worksheet Templates',
      description: 'What the reader fills in while reading a study.',
      count: worksheets?.count,
      defaultName: nameOf(worksheets?.results, settings.defaultWorksheet),
      action: 'Manage Worksheets',
      path: '/templates/worksheets',
    },
    {
      title: 'Report Templates',
      description: 'How the final PDF report is laid out.',
      count: reports?.count,
      defaultName: nameOf(reports?.results, settings.defaultTemplate),
      action: 'Manage Reports',
      path: '/templates/templates',
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.cards}>
        {cards.map((card) => (
          <Card key={card.title}>
            <CardHeader title={card.title} description={card.description} />
            <CardContent>
              <div className={styles.count}>{card.count ?? '—'}</div>
              <Text type="secondary">
                Your default: <Text strong>{card.defaultName}</Text>
              </Text>
            </CardContent>
            <CardFooter>
              <Button type="primary" onClick={() => navigate(card.path)}>
                {card.action}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader title="Recently Updated" />
        <CardContent>
          {recent.length === 0 ? (
            <Text type="secondary">No templates yet</Text>
          ) : (
            <ul className={styles.recent}>
              {recent.map((item) => (
                <li key={`${item.kind}-${item.uuid}`}>
                  <button
                    type="button"
                    className={styles.recentRow}
                    onClick={() => navigate(item.path)}
                  >
                    <Text strong>{item.name}</Text>
                    <Tag>
                      {item.kind === 'worksheet' ? 'Worksheet' : 'Report'}
                    </Tag>
                    <Text type="secondary" className={styles.date}>
                      {formatDate(item.updatedAt)}
                    </Text>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
